import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Utils, authJsonHeaders, formatDateTime, formatDate,
    handleSessionExpired, isCheckoutInvoice, getInvoiceStatusDisplay, getInvoiceKindDisplay, getBillingStatusDisplay, METRIC_TYPE_CODES,
    PLAN_TYPE_CODES, INVOICE_STATUS_CODES, bigAmountFormat, can, isBillingStatus,
    getDefaultPageSize,
    formatMetricPrice,
} from '../../utils';
import BillView from './billView';
import ListPaginationBar from '../common/ListPaginationBar';
import {
    MdReceipt, MdDashboard, MdPictureAsPdf, MdVisibility, MdErrorOutline, MdPayment,
} from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import Modal from '../common/Modal';
import Swal from 'sweetalert2';
import { useActionDropdown } from '../../hooks/useActionDropdown';

function loadRazorpayScript() {
    if (typeof window === 'undefined') return Promise.reject(new Error('no_window'));
    if (window.Razorpay) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('razorpay_script_failed'));
        document.body.appendChild(script);
    });
}

const INVOICE_PAID = INVOICE_STATUS_CODES[1]?.value ?? 1;
const METRIC_CODES = (METRIC_TYPE_CODES || []).filter((m) => m.value > 0);

const TABS = [
    { key: 'overview', label: 'Overview', icon: <MdDashboard size={15} /> },
    { key: 'invoices', label: 'Invoices', icon: <MdReceipt   size={15} /> },
];

const Billing = () => {
    const pageSize = getDefaultPageSize();
    const location = useLocation();
    const navigate = useNavigate();
    const payParam = new URLSearchParams(location.search).get('pay');
    const autoPayHandledRef = useRef(null);
    const [tab, setTab] = useState(payParam ? 'invoices' : 'overview');
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [usageApps, setUsageApps] = useState([]);
    const [usageTotals, setUsageTotals] = useState(null);
    const [usageRates, setUsageRates] = useState({});
    const [usageMeta, setUsageMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [usagePage, setUsagePage] = useState(1);
    const [usagePeriod, setUsagePeriod] = useState(null);
    const [usageCurrencySymbol, setUsageCurrencySymbol] = useState('');
    const [usageBillingCycle, setUsageBillingCycle] = useState('monthly');
    const [usageLoading, setUsageLoading] = useState(false);
    const [planHistory, setPlanHistory] = useState([]);
    const [planHistoryPage, setPlanHistoryPage] = useState(1);
    const [planHistoryMeta, setPlanHistoryMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [planHistoryLoading, setPlanHistoryLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceMeta, setInvoiceMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [hasPendingUsageBill, setHasPendingUsageBill] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceDetail, setInvoiceDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [paymentErrorsInvoice, setPaymentErrorsInvoice] = useState(null);
    const [paymentErrors, setPaymentErrors] = useState([]);
    const [paymentErrorsLoading, setPaymentErrorsLoading] = useState(false);
    const [payingInvoiceId, setPayingInvoiceId] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const invoiceTableScrollRef = useRef(null);
    const {
        openActionId,
        openActionItem,
        dropdownPosition,
        dropdownRef,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
      tableScrollRef: invoiceTableScrollRef,
      optionCount: (item) =>
        2 +
        (item?.has_payment_errors || Number(item?.payment_attempts_count) > 0 ? 1 : 0) +
        (item?.can_pay && can("billing.manage") ? 1 : 0),
      menuWidth: 220,
    });

    const userData = (() => {
        try { return JSON.parse(localStorage.getItem('userData') || 'null'); } catch { return null; }
    })();

    const fetchSummary = useCallback(async () => {
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/summary`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: '{}',
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                setSummary(r.data);
                setHasPendingUsageBill(Boolean(r.data?.has_pending_usage_bill));
            }
        } catch { toast.error('Could not load billing summary'); }
    }, []);

    const fetchUsageApps = useCallback(async (page = 1) => {
        setUsageLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/usage-apps`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({ page, limit: pageSize }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                setUsageApps(r.data || []);
                setUsageTotals(r.totals || null);
                setUsagePeriod(r.period || null);
                setUsageCurrencySymbol(r.currency_symbol || '');
                setUsageBillingCycle(r.billing_cycle || 'monthly');
                const rateMap = {};
                for (const row of r.rates || []) {
                    rateMap[Number(row.type)] = parseFloat(row.price) || 0;
                }
                setUsageRates(rateMap);
                const p = r.pagination || {};
                setUsagePage(Number(p.page) || page);
                setUsageMeta({
                    page: Number(p.page) || page,
                    totalPages: Number(p.totalPages) || 1,
                    total: Number(p.total) || 0,
                });
            }
        } catch { toast.error('Could not load usage'); }
        finally { setUsageLoading(false); }
    }, [pageSize]);

    const fetchPlanHistory = useCallback(async (page = 1) => {
        setPlanHistoryLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/plan-history`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({ page, limit: pageSize }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                setPlanHistory(r.data || []);
                const p = r.pagination || {};
                setPlanHistoryPage(Number(p.page) || page);
                setPlanHistoryMeta({
                    page: Number(p.page) || page,
                    totalPages: Number(p.totalPages) || 1,
                    total: Number(p.total) || 0,
                });
            }
        } catch { toast.error('Could not load plan history'); }
        finally { setPlanHistoryLoading(false); }
    }, [pageSize]);

    const fetchInvoices = useCallback(async (page = 1) => {
        setInvoicesLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/invoices`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({ page, limit: pageSize }),
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                setInvoices(r.data || []);
                setHasPendingUsageBill(Boolean(r.has_pending_usage_bill));
                const p = r.pagination || {};
                setInvoicePage(Number(p.page) || page);
                setInvoiceMeta({
                    page: Number(p.page) || page,
                    totalPages: Number(p.totalPages) || 1,
                    total: Number(p.total) || 0,
                });
            }
        } catch {}
        finally { setInvoicesLoading(false); }
    }, [pageSize]);

    const loadPageData = useCallback(async () => {
        setLoading(true);
        await fetchSummary();
        setLoading(false);
    }, [fetchSummary]);

    const loadTabLists = useCallback(async () => {
        if (tab === 'overview') {
            await Promise.all([fetchUsageApps(1), fetchPlanHistory(1)]);
        } else if (tab === 'invoices') {
            await fetchInvoices(1);
        }
    }, [tab, fetchUsageApps, fetchPlanHistory, fetchInvoices]);

    const handleRefresh = async () => {
        await loadPageData();
        await loadTabLists();
    };

    const handleCancelSubscription = async () => {
        if (hasPendingUsageBill) {
            toast.error('Please pay your pending bill before cancelling the subscription.');
            setTab('invoices');
            return;
        }
        const confirm = await Swal.fire({
            title: "Cancel subscription?",
            text: "You won't be charged again. Your current plan keeps working until the end of this billing period; after that it becomes Cancelled and Free starts.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Yes, cancel',
            cancelButtonText: 'Keep subscription',
        });
        if (!confirm.isConfirmed) return;
        setCancelling(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/cancel-subscription`, {
                method: 'POST', headers: authJsonHeaders(), body: '{}',
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) {
                toast.success(r.message || 'Subscription cancelled');
                await handleRefresh();
            } else {
                toast.error(r.message || 'Could not cancel subscription');
                if (r.code === 'PENDING_INVOICE') setTab('invoices');
            }
        } catch {
            toast.error('Could not cancel subscription');
        } finally {
            setCancelling(false);
        }
    };

    useEffect(() => { loadPageData(); }, [loadPageData]);
    useEffect(() => { loadTabLists(); }, [loadTabLists]);

    const openInvoiceDetail = async (invoice) => {
        setSelectedInvoice(invoice); setInvoiceDetail(null); setDetailLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/invoices/${invoice.id}`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: '{}',
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (r.status) setInvoiceDetail(r.data);
            else toast.error(r.message || 'Could not load invoice');
        } catch { toast.error('Could not load invoice'); }
        setDetailLoading(false);
    };

    const openPaymentErrors = async (invoice) => {
        setPaymentErrorsInvoice(invoice);
        setPaymentErrors([]);
        setPaymentErrorsLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/invoices/${invoice.id}`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: '{}',
            });
            const r = await res.json();
            if (handleSessionExpired(res, r)) return;
            if (!r.status) {
                toast.error(r.message || 'Could not load payment errors');
                return;
            }
            const attempts =
                r.data?.invoice?.payment_attempts ||
                r.data?.invoice?.usage_breakdown?.payment_attempts ||
                [];
            const lastErr = r.data?.invoice?.last_payment_error;
            if (Array.isArray(attempts) && attempts.length) {
                setPaymentErrors(attempts);
            } else if (lastErr && String(lastErr.error_description || '').trim()) {
                setPaymentErrors([lastErr]);
            } else {
                setPaymentErrors([]);
            }
        } catch {
            toast.error('Could not load payment errors');
        } finally {
            setPaymentErrorsLoading(false);
        }
    };

    const recordManualPaymentFailure = async (invoiceId, orderId, err) => {
        try {
            await fetch(`${Utils.baseurl}user/billing/invoices/${invoiceId}/payment-attempts`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({
                    source: 'manual',
                    order_id: orderId || null,
                    payment_id: err?.metadata?.payment_id || err?.payment_id || null,
                    razorpay_status: 'failed',
                    error_code: err?.code || null,
                    error_reason: err?.reason || null,
                    error_description: err?.description || err?.reason || 'Payment failed',
                }),
            });
        } catch { /* best-effort */ }
    };

    const downloadInvoicePdf = async (invoiceId) => {
        try {
            const res = await fetch(`${Utils.baseurl}user/billing/invoices/${invoiceId}/pdf`, {
                headers: authJsonHeaders(),
            });
            if (handleSessionExpired(res, null)) return;
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message || 'Could not download invoice PDF');
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `notifySphere-Invoice-${invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('Could not download invoice PDF');
        }
    };

    const handlePayInvoice = async (invoice) => {
        if (!userData?.id || !invoice?.id) return;
        setPayingInvoiceId(invoice.id);
        try {
            await loadRazorpayScript();
            const oRes = await fetch(`${Utils.baseurl}payment/pay-invoice`, { method: 'POST', headers: authJsonHeaders(), body: JSON.stringify({ invoice_id: invoice.id }) });
            const oData = await oRes.json();
            if (!oData.status || !oData.data?.order_id) { toast.error(oData.message || 'Could not start payment'); setPayingInvoiceId(null); return; }
            const {
                key_id, order_id, amount, currency, site_name, plan_id, billing_cycle,
                customer_id, recurring,
            } = oData.data;
            const rzpOptions = {
                key: key_id, amount, currency, name: site_name,
                description: `Invoice #${invoice.id}`, order_id,
                prefill: { email: userData.email || '', name: userData.name || userData.first_name || '' },
                theme: { color: '#4346CE' },
                ...(customer_id ? { customer_id } : {}),
                ...(Number(recurring) === 1 ? { recurring: 1 } : {}),
                handler: async (response) => {
                    try {
                        const vRes = await fetch(`${Utils.baseurl}payment/verify-payment`, { method: 'POST', headers: authJsonHeaders(), body: JSON.stringify({ userId: userData.id, plan_id, billing_cycle: billing_cycle || 'monthly', ...response }) });
                        const vData = await vRes.json();
                        if (vData.status) {
                            toast.success('Invoice paid successfully');
                            await Promise.all([fetchSummary(), fetchInvoices(invoicePage)]);
                        } else toast.error(vData.message || 'Payment verification failed');
                    } catch { toast.error('Payment verification failed'); }
                    finally { setPayingInvoiceId(null); }
                },
                modal: { ondismiss: () => setPayingInvoiceId(null) },
            };
            const rzp = new window.Razorpay(rzpOptions);
            rzp.on('payment.failed', async (r) => {
                const err = r?.error || {};
                toast.error(err.description || 'Payment failed');
                await recordManualPaymentFailure(invoice.id, order_id, err);
                await fetchInvoices(invoicePage);
                setPayingInvoiceId(null);
            });
            rzp.open();
            // "Opening…" is only meant to cover the order-creation wait above — once Razorpay's
            // own modal is up it has its own loading/error UI, so don't leave our button stuck
            // on "Opening…" waiting for a handler/dismiss/failed event that may never fire (e.g.
            // an order-level validation error inside the modal, which is neither a dismiss nor a
            // payment.failed).
            setPayingInvoiceId(null);
        } catch { toast.error('Could not open payment gateway'); setPayingInvoiceId(null); }
    };


    // Deep link from the pending-bill banner: /billing?pay=<invoice_id> opens the Invoices tab
    // and starts the same Razorpay flow as the row's "Pay now" action.
    useEffect(() => {
        if (!payParam || invoicesLoading || autoPayHandledRef.current === payParam) return;
        if (tab !== 'invoices') { setTab('invoices'); return; }
        if (!invoices.length) return;
        autoPayHandledRef.current = payParam;
        const target = invoices.find((inv) => String(inv.id) === String(payParam));
        navigate('/billing', { replace: true });
        if (target?.can_pay && can('billing.manage')) handlePayInvoice(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payParam, tab, invoices, invoicesLoading]);

    const sub = summary?.subscription;
    const isFreePlan = Number(summary?.plan_type ?? sub?.plan_type ?? PLAN_TYPE_CODES[0].value) === PLAN_TYPE_CODES[0].value;
    const currencySymbol = usageCurrencySymbol || sub?.currency_symbol || '';
    const period = usagePeriod || summary?.period;
    const showUsageBill = Boolean(summary?.show_usage_bill);

    const S = {
        page: { width: '100%' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
        card: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '20px 24px', marginBottom: 16 },
        label: { margin: '0 0 14px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
        tabBar: { display: 'flex', gap: 4, marginBottom: 24, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' },
        th: { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6' },
        td: { padding: '11px 14px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f9fafb' },
    };

    return (
        <div style={S.page}>
            <div style={S.header}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827' }}>Billing</h1>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>Your plan, usage &amp; invoices</p>
                </div>
                <div className="add_app">
                    <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={handleRefresh}>
                        <FiRefreshCw />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            <div style={S.tabBar}>
                {TABS.map((t) => (
                    <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all .12s', background: tab === t.key ? '#fff' : 'transparent', color: tab === t.key ? '#4f46e5' : '#6b7280', boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none' }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9ca3af' }}>Loading…</div>
            ) : (
                <>
                    {tab === 'overview' && (
                        // Scroll only within this tab's own content area, not the whole page —
                        // keeps the header/tab bar above fixed in place.
                        <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', paddingRight: 4 }}>
                            <div style={S.card}>
                                <p style={S.label}>Current plan</p>
                                {sub ? (() => {
                                    const billingStatus = getBillingStatusDisplay(sub.status);
                                    return (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 16, alignItems: 'start' }}>
                                        {(() => {
                                            const lp = summary?.last_payment || sub.last_payment;
                                            return [
                                                ['Plan', sub.plan_name || '—'],
                                                ['Status', (
                                                    <span className={billingStatus.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                                        {billingStatus.label}
                                                    </span>
                                                )],
                                                ['Billing cycle', <span style={{ textTransform: 'capitalize' }}>{sub.billing_cycle || usageBillingCycle || 'monthly'}</span>],
                                                ['Next bill', formatDate(sub.next_billing_date) || '—'],
                                                ['Last payment', lp?.paid_at ? bigAmountFormat(lp.amount, lp.currency_symbol || currencySymbol) : '—'],
                                                ['Last payment date', lp?.paid_at ? formatDate(lp.paid_at) : '—'],
                                                ['Current period', period ? `${formatDate(period.start)} – ${formatDate(period.end)}` : '—'],
                                            ];
                                        })().map(([label, val]) => (
                                            <div key={label} style={label === 'Current period' ? { minWidth: 200 } : undefined}>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>{label}</div>
                                                {label === 'Status' ? val : (
                                                    <strong style={{ fontSize: 14, color: '#111827', whiteSpace: 'nowrap' }}>
                                                        {val}
                                                    </strong>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    );
                                })() : (
                                    <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>No active subscription.</p>
                                )}

                                {summary?.upcoming_plan && (
                                    <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: '#eef2ff', border: '1px solid #e0e7ff', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 13, color: '#4338ca' }}>
                                            Currently on <strong>{sub?.plan_name || '—'}</strong>. <strong>{summary.upcoming_plan.plan_name}</strong> is scheduled to start on {formatDate(summary.upcoming_plan.apply_at)}.
                                        </span>
                                    </div>
                                )}

                                {can("billing.manage") && sub && !isFreePlan && !isBillingStatus(sub.status, 3) && (
                                    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                                        {sub.cancel_at_period_end ? (
                                            <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
                                                Your subscription is set to cancel and will switch to Free
                                                {sub.next_billing_date ? ` on ${formatDate(sub.next_billing_date)}` : ' at the end of the current period'}.
                                            </p>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={handleCancelSubscription}
                                                    disabled={cancelling || hasPendingUsageBill}
                                                    style={{ padding: '8px 16px', border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', cursor: (cancelling || hasPendingUsageBill) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, opacity: (cancelling || hasPendingUsageBill) ? 0.6 : 1 }}
                                                >
                                                    {cancelling ? 'Cancelling…' : 'Cancel subscription'}
                                                </button>
                                                {hasPendingUsageBill && (
                                                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b45309' }}>
                                                        Pay your pending bill first, then you can cancel.
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={S.card}>
                                <p style={S.label}>Usage per app</p>
                                {usageLoading ? (
                                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: 24, margin: 0 }}>Loading…</p>
                                ) : (
                                    <>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={S.th}>App</th>
                                                        {METRIC_CODES.map((m) => (
                                                            <th key={m.value} style={{ ...S.th, textAlign: 'right' }}>{m.label}</th>
                                                        ))}
                                                        {showUsageBill && (
                                                            <th style={{ ...S.th, textAlign: 'right' }}>Bill</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {usageApps.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={METRIC_CODES.length + (showUsageBill ? 2 : 1)} style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: 32 }}>
                                                                No usage data yet
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        <>
                                                            {usageApps.map((app) => (
                                                                <tr key={app.application_id}>
                                                                    <td style={S.td}>
                                                                        <strong>{app.application_name}</strong>
                                                                        {app.application_id === 0 && (
                                                                            <span style={{ marginLeft: 6, fontSize: 11, color: '#9ca3af' }}>(account level)</span>
                                                                        )}
                                                                    </td>
                                                                    {METRIC_CODES.map((m) => {
                                                                        const count = Number(app.metrics?.[m.value] || 0);
                                                                        if (!showUsageBill || count === 0) {
                                                                            return (
                                                                                <td key={m.value} style={{ ...S.td, textAlign: 'right', color: count > 0 ? '#111827' : '#d1d5db', fontWeight: count > 0 ? 500 : 400 }}>
                                                                                    {count.toLocaleString()}
                                                                                </td>
                                                                            );
                                                                        }
                                                                        const rate = Number(usageRates[m.value] || 0);
                                                                        const amount = Number(app.amounts?.[m.value] || 0);
                                                                        return (
                                                                            <td key={m.value} style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap', color: '#111827', fontSize: 12 }}>
                                                                                {count.toLocaleString()} × {formatMetricPrice(currencySymbol, rate)} = {bigAmountFormat(amount, currencySymbol)}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                    {showUsageBill && (
                                                                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>
                                                                            {bigAmountFormat(app.bill, currencySymbol)}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                            {usageTotals && (
                                                                <tr style={{ background: '#f9fafb' }}>
                                                                    <td style={{ ...S.td, fontWeight: 700, borderBottom: 'none' }}>Total</td>
                                                                    {METRIC_CODES.map((m) => {
                                                                        const count = Number(usageTotals.metrics?.[m.value] || 0);
                                                                        if (!showUsageBill || count === 0) {
                                                                            return (
                                                                                <td key={m.value} style={{ ...S.td, textAlign: 'right', fontWeight: 700, borderBottom: 'none', color: count > 0 ? '#111827' : '#d1d5db' }}>
                                                                                    {count.toLocaleString()}
                                                                                </td>
                                                                            );
                                                                        }
                                                                        const rate = Number(usageRates[m.value] || 0);
                                                                        const amount = Number(usageTotals.amounts?.[m.value] || 0);
                                                                        return (
                                                                            <td key={m.value} style={{ ...S.td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: 'none', fontSize: 12 }}>
                                                                                {count.toLocaleString()} × {formatMetricPrice(currencySymbol, rate)} = {bigAmountFormat(amount, currencySymbol)}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                    {showUsageBill && (
                                                                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: '#4f46e5', borderBottom: 'none' }}>
                                                                            {bigAmountFormat(usageTotals.bill, currencySymbol)}
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <ListPaginationBar
                                            page={usagePage}
                                            setPage={fetchUsageApps}
                                            total={usageMeta.total}
                                            pageSize={pageSize}
                                            totalPages={usageMeta.totalPages}
                                            ariaLabel="Usage per app pagination"
                                        />
                                    </>
                                )}
                            </div>

                            <div style={S.card}>
                                <p style={S.label}>Plan history</p>
                                {planHistoryLoading ? (
                                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: 24, margin: 0 }}>Loading…</p>
                                ) : (
                                    <>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                                                <thead>
                                                    <tr>
                                                        {['Plan', 'Cycle', 'Status', 'Purchased'].map((h) => (
                                                            <th key={h} style={S.th}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {planHistory.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="4" style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: 32 }}>No plan history</td>
                                                        </tr>
                                                    ) : planHistory.map((h) => (
                                                        <tr key={h.id}>
                                                            <td style={S.td}><strong>{h.plan_name || '—'}</strong></td>
                                                            <td style={{ ...S.td, textTransform: 'capitalize' }}>{h.billing_cycle || 'monthly'}</td>
                                                            <td style={S.td}>
                                                                <span className={h.status_badge_class || 'badge rounded-pill bg-light-secondary'}>
                                                                    {h.status_label || '—'}
                                                                </span>
                                                            </td>
                                                            <td style={{ ...S.td, whiteSpace: 'nowrap', color: '#6b7280' }}>
                                                                {formatDate(h.createdAt) || '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <ListPaginationBar
                                            page={planHistoryPage}
                                            setPage={fetchPlanHistory}
                                            total={planHistoryMeta.total}
                                            pageSize={pageSize}
                                            totalPages={planHistoryMeta.totalPages}
                                            ariaLabel="Plan history pagination"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'invoices' && (
                        <div style={S.card}>
                            <p style={S.label}>Invoices</p>
                            {invoicesLoading ? (
                                <p style={{ textAlign: 'center', color: '#9ca3af', padding: 24, margin: 0 }}>Loading…</p>
                            ) : (
                                <>
                                    <div ref={invoiceTableScrollRef} style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                                            <thead>
                                                <tr>
                                                    {['Type', 'Plan', 'Period', 'Amount', 'Status', 'Error', 'Paid', 'Actions'].map((h) => (
                                                        <th key={h} style={{ ...S.th, textAlign: h === 'Amount' || h === 'Actions' ? 'right' : 'left' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoices.length === 0 ? (
                                                    <tr><td colSpan="8" style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: 32 }}>No invoices yet</td></tr>
                                                ) : invoices.map((inv) => {
                                                    const isPaid = Number(inv.status) === INVOICE_PAID;
                                                    const statusUi = getInvoiceStatusDisplay(inv.status);
                                                    const kindUi = getInvoiceKindDisplay(inv.invoice_kind);
                                                    const showError = !isPaid && inv.has_payment_errors && inv.last_payment_error;
                                                    const errText = showError
                                                        ? [
                                                            inv.last_payment_error.error_reason || inv.last_payment_error.error_code || 'Auto-pay failed',
                                                            inv.last_payment_error.error_description
                                                                ? String(inv.last_payment_error.error_description)
                                                                : null,
                                                        ].filter(Boolean).join(': ')
                                                        : '';
                                                    return (
                                                    <tr key={inv.id}>
                                                        <td style={S.td}>
                                                            <span className={kindUi.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                                                {kindUi.label}
                                                            </span>
                                                        </td>
                                                        <td style={S.td}>{inv.plan_name || '—'}</td>
                                                        <td style={{ ...S.td, whiteSpace: 'nowrap', color: '#6b7280' }}>{isCheckoutInvoice(inv) ? '—' : `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`}</td>
                                                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{bigAmountFormat(inv.amount, inv.currency_symbol)}</td>
                                                        <td style={S.td}>
                                                            <span className={statusUi.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                                                {statusUi.label}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...S.td, maxWidth: 260 }}>
                                                            {showError ? (
                                                                <span
                                                                    style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.35, display: 'inline-block' }}
                                                                    title={inv.last_payment_error.error_description || errText}
                                                                >
                                                                    {errText.length > 90 ? `${errText.slice(0, 90)}…` : errText}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af' }}>—</span>
                                                            )}
                                                        </td>
                                                        <td style={{ ...S.td, color: '#9ca3af', whiteSpace: 'nowrap', fontSize: 12 }}>{inv.paid_at ? formatDateTime(inv.paid_at) : '—'}</td>
                                                        <td style={{ ...S.td, textAlign: 'right' }}>
                                                            <div className="support-actions-menu" style={{ position: 'relative', display: 'inline-block' }}>
                                                                <button
                                                                    type="button"
                                                                    className="dropdown-trigger-btn btn btn-link p-0"
                                                                    aria-label="Actions"
                                                                    aria-expanded={openActionId === inv.id}
                                                                    onClick={(e) => handleOpenDropdown(e, inv.id, inv)}
                                                                >
                                                                    <BsThreeDotsVertical />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ListPaginationBar
                                        page={invoicePage}
                                        setPage={fetchInvoices}
                                        total={invoiceMeta.total}
                                        pageSize={pageSize}
                                        totalPages={invoiceMeta.totalPages}
                                        ariaLabel="Invoice pagination"
                                    />
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {openActionItem && dropdownPosition
                ? createPortal(
                    <div
                        ref={dropdownRef}
                        style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: 220 }}
                    >
                        <div className="dropdown-menu-subscription">
                            <button
                                type="button"
                                onClick={() => handleDropdownAction(() => openInvoiceDetail(openActionItem))}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <MdVisibility size={18} style={{ marginRight: 8, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.875rem' }}>View</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDropdownAction(() => downloadInvoicePdf(openActionItem.id))}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <MdPictureAsPdf size={18} style={{ marginRight: 8, flexShrink: 0 }} />
                                <span style={{ fontSize: '0.875rem' }}>Download PDF</span>
                            </button>
                            {(openActionItem.has_payment_errors || Number(openActionItem.payment_attempts_count) > 0) && (
                                <button
                                    type="button"
                                    onClick={() => handleDropdownAction(() => openPaymentErrors(openActionItem))}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <MdErrorOutline size={18} style={{ marginRight: 8, flexShrink: 0, color: '#b91c1c' }} />
                                    <span style={{ fontSize: '0.875rem' }}>Payment errors</span>
                                </button>
                            )}
                            {openActionItem.can_pay && can('billing.manage') && (
                                <button
                                    type="button"
                                    disabled={payingInvoiceId === openActionItem.id}
                                    onClick={() => handleDropdownAction(() => handlePayInvoice(openActionItem))}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <MdPayment size={18} style={{ marginRight: 8, flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.875rem' }}>
                                        {payingInvoiceId === openActionItem.id ? 'Opening…' : 'Pay now'}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>,
                    document.body
                )
                : null}

            {selectedInvoice && (
                <Modal
                    title={`Invoice #${selectedInvoice.id}`}
                    onClose={() => { setSelectedInvoice(null); setInvoiceDetail(null); }}
                    maxWidth="640px"
                    footer={invoiceDetail ? (
                        <>
                            <button
                                type="button"
                                onClick={() => { setSelectedInvoice(null); setInvoiceDetail(null); }}
                                style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151' }}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => downloadInvoicePdf(invoiceDetail.invoice.id)}
                                style={{
                                    padding: '10px 18px',
                                    background: '#4f46e5',
                                    border: 'none',
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#fff',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <MdPictureAsPdf size={16} /> Download PDF
                            </button>
                        </>
                    ) : null}
                >
                        {detailLoading ? (
                            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0' }}>Loading…</p>
                        ) : invoiceDetail ? (
                            <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                                        {[
                                            ['Type', getInvoiceKindDisplay(invoiceDetail.invoice.invoice_kind).label],
                                            ['Amount', bigAmountFormat(invoiceDetail.invoice.amount, invoiceDetail.invoice.currency_symbol)],
                                            ['Status', invoiceDetail.invoice.status_label],
                                            ['Plan', invoiceDetail.invoice.plan_name || '—'],
                                        ].map(([label, val]) => (
                                            <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                                                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>{label}</div>
                                                <strong style={{ fontSize: 14, color: '#111827' }}>{val}</strong>
                                            </div>
                                        ))}
                                    </div>
                                    {Number(invoiceDetail.invoice.status) !== INVOICE_PAID
                                        && invoiceDetail.invoice.has_payment_errors
                                        && invoiceDetail.invoice.last_payment_error ? (
                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#991b1b' }}>
                                            <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 600, marginBottom: 4 }}>Payment error</div>
                                            {[
                                                invoiceDetail.invoice.last_payment_error.error_reason
                                                    || invoiceDetail.invoice.last_payment_error.error_code
                                                    || 'Auto-pay failed',
                                                invoiceDetail.invoice.last_payment_error.error_description
                                                    ? String(invoiceDetail.invoice.last_payment_error.error_description)
                                                    : null,
                                            ].filter(Boolean).join(': ')}
                                        </div>
                                    ) : null}
                                    {!isCheckoutInvoice(invoiceDetail.invoice) && (
                                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Period</div>
                                            <strong>{formatDate(invoiceDetail.invoice.period_start)} – {formatDate(invoiceDetail.invoice.period_end)}</strong>
                                        </div>
                                    )}
                                    {invoiceDetail.invoice.razorpay_payment_id && (
                                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Payment ID</div>
                                            <strong style={{ wordBreak: 'break-all', fontSize: 12 }}>{invoiceDetail.invoice.razorpay_payment_id}</strong>
                                        </div>
                                    )}

                                    <BillView
                                        breakdown={invoiceDetail.invoice.usage_breakdown}
                                        invoiceAmount={invoiceDetail.invoice.amount}
                                        currencySymbol={invoiceDetail.invoice.currency_symbol}
                                        planName={invoiceDetail.invoice.plan_name}
                                        invoiceKind={invoiceDetail.invoice.invoice_kind}
                                    />

                                    {invoiceDetail.rates_applied?.length > 0 && !invoiceDetail.invoice.usage_breakdown?.rates_applied?.length && (
                                        <div>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Rates applied</div>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead><tr style={{ background: '#f9fafb' }}>
                                                    <th style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12 }}>Metric</th>
                                                    <th style={{ padding: '8px 10px', textAlign: 'right', color: '#6b7280', fontWeight: 600, fontSize: 12 }}>Rate</th>
                                                </tr></thead>
                                                <tbody>
                                                    {invoiceDetail.rates_applied.map((r) => (
                                                        <tr key={r.type} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '8px 10px', color: '#374151' }}>{r.type_label}</td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>{r.price}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                        ) : null}
                </Modal>
            )}

            {paymentErrorsInvoice && (
                <Modal
                    title={`Payment errors — Invoice #${paymentErrorsInvoice.id}`}
                    onClose={() => { setPaymentErrorsInvoice(null); setPaymentErrors([]); }}
                    maxWidth="720px"
                    footer={(
                        <button
                            type="button"
                            onClick={() => { setPaymentErrorsInvoice(null); setPaymentErrors([]); }}
                            style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151' }}
                        >
                            Close
                        </button>
                    )}
                >
                    {paymentErrorsLoading ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>Loading…</p>
                    ) : paymentErrors.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>No payment attempts recorded yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {paymentErrors.map((a, idx) => (
                                <div
                                    key={`${a.order_id || 'ord'}-${a.payment_id || 'pay'}-${a.at || idx}`}
                                    style={{ border: '1px solid #fee2e2', background: '#fff7f7', borderRadius: 10, padding: '12px 14px' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                                        <strong style={{ fontSize: 13, color: '#991b1b' }}>
                                            {a.error_reason || a.error_code || a.razorpay_status || 'Failed'}
                                        </strong>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                                            {a.at ? formatDateTime(a.at) : '—'}
                                            {a.source ? ` · ${a.source}` : ''}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                                        <div><span style={{ color: '#9ca3af' }}>Error code:</span> {a.error_code || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Reason:</span> {a.error_reason || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Message:</span> {a.error_description || '—'}</div>
                                        <div><span style={{ color: '#9ca3af' }}>Order ID:</span> <span style={{ wordBreak: 'break-all' }}>{a.order_id || '—'}</span></div>
                                        <div><span style={{ color: '#9ca3af' }}>Payment ID:</span> <span style={{ wordBreak: 'break-all' }}>{a.payment_id || '—'}</span></div>
                                        {a.razorpay_status && (
                                            <div><span style={{ color: '#9ca3af' }}>Razorpay status:</span> {a.razorpay_status}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default Billing;
